import { z } from 'zod';

// ─── PERMISSIONS constant (42 canonical codes) ──────────────────────
// NOTE: If this list changes, also update the seed migration IN-list.
export const PERMISSIONS = {
  MEDIA_UNIT_VIEW: 'media_unit.view',
  MEDIA_UNIT_UPDATE_AVAILABILITY: 'media_unit.update_availability',
  MEDIA_UNIT_BLOCK_AVAILABILITY: 'media_unit.block_availability',
  MEDIA_UNIT_CREATE: 'media_unit.create',
  MEDIA_UNIT_EDIT: 'media_unit.edit',
  MEDIA_UNIT_DELETE: 'media_unit.delete',
  RESERVATION_MANAGE: 'reservation.manage',
  RESERVATION_MANAGE_WAITLIST: 'reservation.manage_waitlist',
  RESERVATION_FORCE_RELEASE: 'reservation.force_release',
  RESERVATION_VIEW: 'reservation.view',
  /**
   * DB-seeded since the reservation-engine capability's seed migration
   * (packages/db/drizzle/0031_reservations.sql), but absent from this
   * canonical constant until manual-reservation-expiry-editing wired it as
   * the sole authorization for the manual effective-expiry edit action
   * (CU-86cb7qgc9). Does not authorize status override, release, date-range
   * editing, queue administration, or availability changes.
   */
  RESERVATION_EXTEND: 'reservation.extend',
  ATTACHMENT_MANAGE: 'attachment.manage',
  ATTACHMENT_VIEW: 'attachment.view',
  SUPPLIER_MANAGE: 'supplier.manage',
  SUPPLIER_VIEW: 'supplier.view',
  MAP_VIEW: 'map.view',
  COST_VIEW: 'cost.view',
  COST_VIEW_HISTORY: 'cost.view_history',
  /**
   * Additional gate on top of `cost.view` for a media unit's `rent` field
   * specifically (rent-cost-visibility-permission, CU-86cbgnt3z). Only takes
   * effect as a fully-wildcard grant — see PolicyService's wildcard-only
   * check — never scoped per media type or ownership. Holding this without a
   * matching `cost.view` scope for the same unit grants nothing: rent is one
   * of the internal costs `cost.view` gates, not an independent capability.
   */
  COST_VIEW_RENT: 'cost.view_rent',
  OFFER_PREPARE: 'offer.prepare',
  OFFER_PUBLISH: 'offer.publish',
  OFFER_DELETE: 'offer.delete',
  OFFER_VIEW_ALL: 'offer.view_all',
  OFFER_APPROVE_ANY: 'offer.approve_any',
  OFFER_SET_STATUS_ANY: 'offer.set_status_any',
  /**
   * DB-seeded via the offer-client-collaboration capability's migration and
   * legacy-permission-seed-data.ts (mirrors the reservation.extend precedent),
   * not part of the 25-code canonical scope catalog. Gates a sales-department
   * caller's ability to set `salespersonId` to someone other than themselves
   * on offer creation (`resolveSalespersonId` in `apps/api`); it does not
   * affect non-sales-department callers, who must always pick a salesperson
   * explicitly regardless of this permission.
   */
  OFFER_ASSIGN_OTHER_SALESPERSON: 'offer.assign_other_salesperson',
  ADMIN_MANAGE_USERS: 'admin.manage_users',
  ADMIN_MANAGE_ROLES: 'admin.manage_roles',
  ADMIN_MANAGE_PERMISSIONS: 'admin.manage_permissions',
  // Non-matrix domain codes
  CLIENT_VIEW: 'client.view',
  CLIENT_CREATE: 'client.create',
  CLIENT_UPDATE: 'client.update',
  CLIENT_DELETE: 'client.delete',
  TAG_VIEW: 'tag.view',
  TAG_CREATE: 'tag.create',
  TAG_UPDATE: 'tag.update',
  TAG_DELETE: 'tag.delete',
  MEDIA_FORMAT_VIEW: 'media_format.view',
  MEDIA_FORMAT_CREATE: 'media_format.create',
  MEDIA_FORMAT_UPDATE: 'media_format.update',
  // Bare action codes (no dot)
  IMPORT: 'import',
  BULK_EDIT: 'bulk_edit',
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ─── Permission bundles ──────────────────────────────────────────────
// A directional coupling rule: whenever `trigger` is granted on a role,
// every code in `requires` SHALL also be granted (same scope). The reverse
// is NOT implied — a role may hold a `requires` member without `trigger`.
//
// e.g. MEDIA_UNIT_BLOCK_AVAILABILITY requires SUPPLIER_VIEW, because the
// availability-import preview screen's supplier dropdown needs it
// (CU-86cakpget). But SUPPLIER_VIEW is held by nearly every role (it's core
// to supplier browsing / offer prep), while MEDIA_UNIT_BLOCK_AVAILABILITY is
// admin-only by deliberate launch decision (CU-86c9vf27j) — enforcing this
// pair symmetrically made every role holding SUPPLIER_VIEW alone unsavable
// via the admin panel, which was never the intent. A mutual coupling, if one
// is ever needed, is expressed as two rules (A requires B, and B requires A).
//
// Add further rules here as coupled permissions surface; both the
// grant-staging editor (grant-helpers.ts) and the server-side scopes-replace
// save path enforce every rule listed here.
export interface PermissionBundleRule {
  readonly trigger: PermissionCode;
  readonly requires: readonly PermissionCode[];
}

export const PERMISSION_BUNDLES = [
  { trigger: PERMISSIONS.MEDIA_UNIT_BLOCK_AVAILABILITY, requires: [PERMISSIONS.SUPPLIER_VIEW] },
] as const satisfies readonly PermissionBundleRule[];

export type PermissionBundle = (typeof PERMISSION_BUNDLES)[number];

/** A bundle rule whose `trigger` is granted while one or more `requires` codes are missing. */
export interface PermissionBundleViolation {
  readonly rule: PermissionBundle;
  readonly missing: readonly PermissionCode[];
}

/**
 * Returns the first rule whose `trigger` is granted while at least one of
 * its `requires` codes is not — i.e. persisting this grant set would leave a
 * role holding a trigger permission without a permission it requires.
 * Returns `null` when every rule's trigger is either ungranted or fully
 * satisfied. Holding a `requires` code without its `trigger` is never a
 * violation (see the directional note above `PERMISSION_BUNDLES`).
 */
export function findPartialPermissionBundle(
  grantedCodes: readonly string[],
): PermissionBundleViolation | null {
  const granted = new Set(grantedCodes);
  for (const rule of PERMISSION_BUNDLES) {
    if (!granted.has(rule.trigger)) continue;
    const missing = rule.requires.filter((code) => !granted.has(code));
    if (missing.length > 0) {
      return { rule, missing };
    }
  }
  return null;
}

/**
 * Structured `details` payload for the 400 PERMISSION_BUNDLE_INCOMPLETE error
 * thrown by `assertPermissionBundleComplete` (apps/api). Identifies exactly
 * which bundle is broken and which of its members are granted vs missing, so
 * the client can render an actionable message ("X requires Y") instead of the
 * generic "must be granted or revoked together" fallback. Generalizes to any
 * entry in PERMISSION_BUNDLES, including bundles with more than two members.
 */
export const PermissionBundleIncompleteDetailsSchema = z.object({
  bundle: z.array(z.string()) as z.ZodType<PermissionCode[]>,
  grantedCodes: z.array(z.string()) as z.ZodType<PermissionCode[]>,
  missingCodes: z.array(z.string()) as z.ZodType<PermissionCode[]>,
});
export type PermissionBundleIncompleteDetails = z.infer<
  typeof PermissionBundleIncompleteDetailsSchema
>;

// ─── OfferDepartment constant and schema ─────────────────────────────

export const OFFER_DEPARTMENTS = ['sales', 'planners', 'new_products'] as const;
export type OfferDepartment = (typeof OFFER_DEPARTMENTS)[number];
export const OfferDepartmentSchema = z.enum(OFFER_DEPARTMENTS);

// ─── Scope schemas ───────────────────────────────────────────────────

// NOTE: ScopeOwnershipSchema is distinct from OwnershipSchema in availability.ts
// which uses { mine, other }. This schema uses { RW, BROKER } for permission scopes.
export const ScopeOwnershipSchema = z.enum(['RW', 'BROKER']);
export type ScopeOwnership = z.infer<typeof ScopeOwnershipSchema>;

export const PermissionScopeEntrySchema = z.object({
  // z.enum would require listing all 38 codes as a literal tuple, duplicating
  // the PERMISSIONS catalog. Cast narrows the inferred type to PermissionCode
  // while validating shape as a string; the value space is the seeded catalog.
  code: z.string() as z.ZodType<PermissionCode>,
  mediaType: z.string().nullable(),
  ownership: ScopeOwnershipSchema.nullable(),
  department: OfferDepartmentSchema.nullable(),
});
export type PermissionScopeEntry = z.infer<typeof PermissionScopeEntrySchema>;

// ─── Existing schemas ────────────────────────────────────────────────

export const PermissionSchema = z.object({
  id: z.number(),
  code: z.string(),
  resource: z.string(),
  action: z.string(),
  name: z.string(),
  description: z.string().nullable(),
});
export type Permission = z.infer<typeof PermissionSchema>;

export const PermissionGroupedSchema = z.record(z.string(), z.array(PermissionSchema));
export type PermissionGrouped = z.infer<typeof PermissionGroupedSchema>;

/**
 * A single scope grant row returned by GET /roles/:id/scopes.
 * Mirrors the ScopeRow shape from @ooh/db without importing it.
 */
export const RoleScopeRowSchema = z.object({
  permissionCode: z.string(),
  mediaType: z.string().nullable(),
  ownership: ScopeOwnershipSchema.nullable(),
  department: OfferDepartmentSchema.nullable(),
});
export type RoleScopeRow = z.infer<typeof RoleScopeRowSchema>;
